import {
  chakra,
  ComponentMultiStyleConfig,
  Link,
  Select,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { ReactElement, useCallback, useMemo } from "react";
import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../lib/styles/props/component-style-props";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { hasValue, isNullish } from "../../../lib/util/common-util";
import { Link as RouterLink } from "react-router-dom";

export type DropdownOption<T extends string | number = string> = {
  label: string;
  value: T;
};

export type CMFormInputBaseDropDownProps<T extends string | number = string> = {
  editMode?: "editable" | "disabled" | "readOnly";
  variant?: "default" | "diff"; // エラーモードは非対応。
  size?: "sm" | "md" | "lg";

  /**
   * プレースホルダ。
   * nullableがtrueの場合のみ使用する。
   */
  placeholder?: string;

  /**
   * これがtrueの場合のみ、未選択状態(null)を選択可能とする。
   * デフォルト: false
   */
  nullable?: boolean;

  /**
   * ドロップダウン表示用のデータ
   */
  options: DropdownOption<T>[];

  /**
   * 値。
   */
  value?: T | null;

  /**
   * 差分モード時の変更前の値。
   * 差分モードの場合のみ必須。
   */
  changedFrom?: T | null;

  /**
   * 読み取り専用モードのみ使用
   * 表示内容をリンク表示するために使用
   */
  url?: string;

  icon?: ReactElement;

  onChange?: (value: string | null) => void;
  onChangeAsNumber?: (value: number | null) => void;
  onBlur?: () => void;
} & ComponentStyleProps;

/**
 * Dropdownコンポーネント.
 */
export const CMFormInputBaseDropDown = <T extends string | number = string>({
  editMode = "editable",
  variant = "default",
  size = "md",

  placeholder,
  nullable = false,

  options,

  value = null,
  changedFrom = null,
  url,
  icon = <ChevronDownIcon />,

  onChange,
  onChangeAsNumber,
  onBlur,

  sx,
  ...rest
}: CMFormInputBaseDropDownProps<T>) => {
  const styles = useMultiStyleConfig("CMFormInputBaseDropDownStyles", {
    size,
  });

  const _placeholder = useMemo(() => {
    // 空文字列だと未指定と同じになり、nullableでも未選択を選択できなくなるため、スペースを一個入れる。
    if (isNullish(placeholder) || placeholder === "") return " ";
    return placeholder;
  }, [placeholder]);

  const isDiffMode = useMemo(() => variant === "diff", [variant]);

  const disabled = useMemo(
    () => editMode === "disabled" || editMode === "readOnly",
    [editMode],
  );

  const labelOfSelectedValue = useMemo(() => {
    if (isNullish(value)) return "";
    const option = options.find((o) => o.value === value);
    return hasValue(option) ? option.label : "";
  }, [options, value]);

  /**
   * 差分モード時、値が変更前と異なるならtrue。
   * 以下のいずれかの場合はfalse。
   * - 差分モードではない。
   * - 変更前と値が同一である。
   */
  const hasChangedInDiffMode = useMemo(
    () => isDiffMode && value !== changedFrom,
    [isDiffMode, value, changedFrom],
  );

  const inputStyles = useMemo(
    () => ({
      ...styles.containerBase,
      ...(hasChangedInDiffMode ? styles.diff : undefined),
      ...sx,
    }),
    [styles, hasChangedInDiffMode, sx],
  );
  const readonlyStyles = useMemo(
    () => ({
      ...styles.readonly,
      ...(hasChangedInDiffMode ? styles.diff : undefined),
      ...sx,
    }),
    [styles, hasChangedInDiffMode, sx],
  );

  /**
   * 値に対応するラベルを取得する。
   * @param value 値
   * @return ラベル
   */
  const labelOf = useCallback(
    (value: T | null) => {
      if (isNullish(value)) return "";
      const option = options.find((o) => o.value === value);
      return hasValue(option) ? option.label : "";
    },
    [options],
  );

  /**
   * Selectの入力値を取得し、呼び出し元に返却
   * @param e 入力イベント
   */
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      // 未選択の場合はnullとする
      const v = e.target.value === "" ? null : e.target.value;

      onChange?.(v);

      if (isNullish(onChangeAsNumber)) return;

      const intValue = hasValue(v) ? Number.parseInt(v) : null;
      if (isNullish(intValue) || isNaN(intValue)) {
        onChangeAsNumber(null);
      } else {
        onChangeAsNumber(intValue);
      }
    },
    [onChange, onChangeAsNumber],
  );

  return (
    <>
      {editMode === "readOnly" ? (
        hasValue(url) ? (
          // リンク先が設定されている場合は、遷移できるようにする
          <Link
            as={RouterLink}
            to={url}
            sx={{ ...styles.link, ...sx }}
            {...rest}
          >
            {labelOf(value)}
          </Link>
        ) : (
          <Text sx={readonlyStyles} {...rest}>
            {labelOfSelectedValue}
          </Text>
        )
      ) : (
        <Select
          disabled={disabled}
          size={size}
          placeholder={nullable ? _placeholder : undefined}
          value={value ?? undefined}
          icon={icon}
          onChange={handleChange}
          onBlur={onBlur}
          sx={inputStyles}
          {...rest}
        >
          {options.map((option, index) => (
            <chakra.option
              key={index}
              value={option.value}
              sx={styles.optionSelected}
            >
              {option.label}
            </chakra.option>
          ))}
        </Select>
      )}
    </>
  );
};

export const CMFormInputBaseDropDownStyles: ComponentMultiStyleConfig = {
  parts: [
    "containerBase",
    // "optionNoSelect",
    "optionSelected",
    "link",
    "readonly",
    "diff",
  ],
  baseStyle: {
    containerBase: { bgColor: "white" },
    optionSelected: { color: "gray.900" },
    link: {
      color: "blue.600",
    },
    readonly: {
      maxW: "max-content",
    },
    diff: {
      borderWidth: "1px",
      borderColor: "orange.500",
      _hover: {
        borderColor: "orange.500",
      },
      _focus: {
        _hover: {
          borderColor: "blue.500",
        },
      },
    },
  },
  sizes: {
    sm: {
      containerBase: {
        h: "22px",
        px: "6px",
      },
    },
    md: {
      containerBase: {
        h: "30px",
        px: "8px",
      },
    },
    lg: {
      containerBase: {
        h: "38px",
        px: "10px",
      },
    },
  },
};
